/* eslint-disable complexity */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, MenuItem, TextField, Button, Chip, Checkbox } from '@mui/material';
import { hasValue, templateKeys, valueMatchesType } from '@scale/shared/corp/ruleEngine';
import {
  ExecutionParameterValue,
  IExecutionParameter,
  ParameterType,
  ValueType,
} from '@scale/shared/types/execution';
import { useCallback, useEffect, useMemo, useState } from 'react';
import InputPresetWrapper from 'react-scale/corp/components/RuleEngine/InputPresetWrapper';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';

const ExecutionParameter = (props: {
  parameterKey: string;
  parameter: IExecutionParameter;
  previousOutputs?: Record<string, IExecutionParameter>;
  editMode?: boolean;
  setParameter?: (key: string, value: IExecutionParameter) => void;
  isCompassParameter?: boolean;
  forceTouched?: boolean;
}) => {
  const {
    parameterKey,
    parameter,
    previousOutputs,
    editMode,
    setParameter,
    isCompassParameter,
    forceTouched,
  } = props;
  const [hasBeenTouched, setHasBeenTouched] = useState<boolean>(false);

  const getDefaultInputValue = useCallback((): string | number | boolean => {
    switch (parameter.valueType) {
      case ValueType.Boolean:
        return false;
      case ValueType.Date:
        return new Date().toString();
      default:
        return '';
    }
  }, [parameter]);

  const [inputValue, setInputValue] = useState<string | number | boolean>(
    hasValue(parameter.value) && !Array.isArray(parameter.value)
      ? (parameter.value as string | number | boolean)
      : getDefaultInputValue(),
  );

  const parameterLabel = useMemo((): string => {
    if (parameter.label) {
      return parameter.label;
    } else if (setParameter) {
      // Add spaces on uppercase letters for camelCase keys
      return parameterKey.replace(/([a-z](?=[A-Z]))/g, '$1 ');
    } else {
      return parameterKey;
    }
  }, [parameterKey, parameter, setParameter]);

  const matchingOutputs = useMemo((): string[] => {
    const matches: string[] = [];
    if (previousOutputs) {
      Object.keys(previousOutputs).map(key => {
        const output = previousOutputs[key];
        if (output.type === parameter.type && output.valueType === parameter.valueType) {
          matches.push(key);
        }
      });
    }
    return matches;
  }, [previousOutputs, parameter]);

  const { inputKeys, indexedInputKeys } = useMemo(() => {
    if (
      parameter.valueType === ValueType.String &&
      parameter.type === ParameterType.Single &&
      parameter.value
    ) {
      return {
        inputKeys: templateKeys(parameter.value.toString()),
        indexedInputKeys: templateKeys(parameter.value.toString(), false, '%'),
      };
    }
    return {
      inputKeys: [],
      indexedInputKeys: [],
    };
  }, [parameter]);

  const { inputs, invalidInputs, notFoundInputs } = useMemo(() => {
    const inputs: string[] = [];
    const invalidInputs: string[] = [];
    const notFoundInputs: string[] = [];
    inputKeys.forEach(key => {
      if (previousOutputs && Object.keys(previousOutputs).includes(key)) {
        inputs.push(`${key}${previousOutputs[key]?.type === ParameterType.Multiple ? '[]' : ''}`);
      } else {
        notFoundInputs.push(key);
      }
    });
    indexedInputKeys.forEach(key => {
      if (previousOutputs && Object.keys(previousOutputs).includes(key)) {
        const previousOutput = previousOutputs[key];
        if (
          previousOutput &&
          previousOutput.type === ParameterType.Multiple &&
          !previousOutput.optional
        ) {
          inputs.push(key);
        } else {
          invalidInputs.push(`${key} is not multiple or is optional`);
        }
      } else {
        notFoundInputs.push(key);
      }
    });
    return {
      inputs,
      invalidInputs,
      notFoundInputs,
    };
  }, [inputKeys, indexedInputKeys, previousOutputs]);

  const inputErrorMessage: string | undefined = useMemo(() => {
    if (hasValue(inputValue)) {
      if (!valueMatchesType(parameter.valueType, inputValue)) {
        return `Must be ${parameter.valueType} format`;
      } else if (typeof inputValue === 'number') {
        if (
          'min' in parameter &&
          (parameter.min || parameter.min === 0) &&
          inputValue < parameter.min
        ) {
          return `Number must be equal or more than ${parameter.min}`;
        }
        if (
          'max' in parameter &&
          (parameter.max || parameter.max === 0) &&
          inputValue > parameter.max
        ) {
          return `Number must be equal or less than ${parameter.max}`;
        }
      } else if (typeof inputValue !== 'boolean') {
        if (
          'minLength' in parameter &&
          parameter.minLength &&
          inputValue.toString().length < parameter.minLength
        ) {
          return `Must be above ${parameter.minLength - 1} characters`;
        }
        if (
          'maxLength' in parameter &&
          parameter.maxLength &&
          inputValue.toString().length > parameter.maxLength
        ) {
          return `Must be below ${parameter.maxLength + 1} characters`;
        }
      }
    }
    return undefined;
  }, [inputValue, parameter]);

  const errorMessage: string | undefined = useMemo(() => {
    if (hasBeenTouched || forceTouched) {
      if (inputErrorMessage) {
        return inputErrorMessage;
      } else if (!parameter.optional && !hasValue(parameter.value)) {
        return 'Required';
      } else if (parameter.valueType === ValueType.String && notFoundInputs.length > 0) {
        return `Some template inputs don't match a previous output`;
      }
    }
    return undefined;
  }, [inputErrorMessage, hasBeenTouched, parameter, notFoundInputs, forceTouched]);

  const getDefaultValue = useCallback((): ExecutionParameterValue => {
    if (parameter.type === ParameterType.Multiple) {
      return [];
    } else {
      switch (parameter.valueType) {
        case ValueType.Boolean:
          return false;
        case ValueType.Date:
          return new Date().toString();
        default:
          return '';
      }
    }
  }, [parameter]);

  useEffect(() => {
    if (
      hasBeenTouched &&
      parameter.type === ParameterType.Single &&
      setParameter &&
      (hasValue(parameter.value) ? inputValue !== parameter.value : hasValue(inputValue))
    ) {
      setParameter &&
        setParameter(parameterKey, {
          ...parameter,
          value: inputValue,
        });
    }
  }, [inputValue, hasBeenTouched]);

  return (
    <div
      style={{
        borderRadius: '7px',
        display: 'flex',
        flex: 1,
        background: 'white',
        flexDirection: 'column',
        minWidth:
          ('multiline' in parameter && parameter.multiline) ||
          (parameter.type === ParameterType.Multiple && (setParameter || hasValue(parameter.value)))
            ? '100%'
            : editMode
            ? 'calc(50% - .4rem)'
            : '200px',
        maxWidth: '100%',
        padding: !setParameter ? '.5rem' : 0,
      }}
    >
      <p
        style={{
          margin: 0,
          lineHeight: 1.5,
          color: '#374151',
          fontSize: '.8rem',
          textTransform: setParameter && !parameter.label ? 'capitalize' : undefined,
        }}
      >
        {parameterLabel}
      </p>
      <InputPresetWrapper
        presetOptions={matchingOutputs}
        selectedOption={parameter.input}
        onSelect={option =>
          setParameter &&
          setParameter(parameterKey, {
            ...parameter,
            value: option ? undefined : (getDefaultValue() as any),
            input: option,
          })
        }
      >
        {setParameter ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'start',
              flexWrap: 'wrap',
              gap: '.5rem',
            }}
          >
            {editMode && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {parameter.type === ParameterType.Multiple && (
                  <p style={{ margin: 0 }}>{`Array<`}</p>
                )}
                <Select
                  size="small"
                  value={parameter.valueType}
                  onChange={e => {
                    const newValueType = e.target.value as ValueType;
                    setParameter(parameterKey, {
                      ...parameter,
                      valueType: newValueType,
                      value: getDefaultValue() as any,
                    });
                  }}
                >
                  <MenuItem value={ValueType.Number}>Number</MenuItem>
                  <MenuItem value={ValueType.String}>String</MenuItem>
                  <MenuItem value={ValueType.Boolean}>Boolean</MenuItem>
                  <MenuItem value={ValueType.ObjectId}>Object Id</MenuItem>
                  <MenuItem value={ValueType.Email}>E-Mail</MenuItem>
                </Select>
                {parameter.type === ParameterType.Multiple && <p style={{ margin: 0 }}>{`>`}</p>}
              </div>
            )}
            {parameter.valueType === ValueType.Boolean ? (
              <Checkbox
                checked={Boolean(inputValue)}
                required={!parameter.optional}
                onChange={(e, checked) => {
                  setHasBeenTouched(true);
                  setInputValue(checked);
                }}
                onClick={() => {
                  if (isCompassParameter) {
                    setHasBeenTouched(true);
                    setInputValue(!inputValue);
                  }
                }}
              />
            ) : 'options' in parameter ? (
              <Select
                size="small"
                style={{
                  width: editMode
                    ? 'auto'
                    : parameter.type === ParameterType.Single
                    ? '100%'
                    : '250px',
                }}
                value={inputValue}
                required={!parameter.optional}
                onChange={e => {
                  setHasBeenTouched(true);
                  setInputValue(e.target.value);
                }}
              >
                {parameter.options &&
                  parameter.options.map(option => (
                    <MenuItem key={`${option.value}`} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
              </Select>
            ) : (
              <TextField
                size="small"
                style={{
                  width: editMode
                    ? 'auto'
                    : parameter.type === ParameterType.Single
                    ? '100%'
                    : '250px',
                }}
                type={
                  parameter.valueType === ValueType.Number ||
                  parameter.valueType === ValueType.Email
                    ? parameter.valueType
                    : 'text'
                }
                name={`${parameterKey}-value`}
                multiline={Boolean('multiline' in parameter && parameter.multiline)}
                rows={4}
                required={!parameter.optional && parameter.type === ParameterType.Single}
                error={Boolean(errorMessage)}
                placeholder={`Enter a ${parameter.valueType}`}
                value={inputValue as string | number}
                onChange={ev => {
                  if (parameter.valueType === ValueType.String || !ev.target.value.includes(' ')) {
                    setHasBeenTouched(true);
                    setInputValue(
                      parameter.valueType === ValueType.Number && ev.target.value.length > 0
                        ? Number(ev.target.value)
                        : ev.target.value,
                    );
                  }
                }}
                helperText={errorMessage}
                onBlur={() => setHasBeenTouched(true)}
              />
            )}
            {parameter.type === ParameterType.Multiple ? (
              <>
                {!inputErrorMessage &&
                  hasValue(inputValue) &&
                  Array.isArray(parameter.value) &&
                  !(parameter.value as any[]).includes(inputValue) && (
                    <div style={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setInputValue(prev => {
                            const newValue = [
                              ...(parameter.value && Array.isArray(parameter.value)
                                ? parameter.value
                                : []),
                              prev,
                            ] as string[] | number[] | boolean[];
                            setParameter(parameterKey, {
                              ...parameter,
                              value: newValue,
                            });
                            return '';
                          });
                        }}
                      >
                        Add
                      </Button>
                    </div>
                  )}
                {Array.isArray(parameter.value) &&
                  parameter.value.map((val, valIndex) => {
                    const onDelete = () => {
                      const values = parameter.value as any[];
                      const index = values.indexOf(val);
                      if (index >= 0) {
                        values.splice(index, 1);
                        setParameter(parameterKey, {
                          ...parameter,
                          value: values,
                        });
                      }
                    };
                    return (
                      <div
                        style={{ display: 'flex', alignItems: 'center', height: '40px' }}
                        key={`${parameterKey}-${val}-${valIndex}`}
                      >
                        {parameter.valueType === ValueType.ObjectId &&
                        typeof val === 'string' &&
                        'objectType' in parameter &&
                        parameter.objectType ? (
                          <SmartLink id={val} type={parameter.objectType} onDelete={onDelete} />
                        ) : (
                          <Chip label={`${val}`} onDelete={onDelete} />
                        )}
                      </div>
                    );
                  })}
              </>
            ) : (
              <>
                {inputs.map(input => (
                  <Chip
                    style={{ paddingLeft: '.5rem' }}
                    label={input}
                    key={input}
                    color="success"
                    icon={<FontAwesomeIcon icon={'check'} />}
                  />
                ))}
                {invalidInputs.map(input => (
                  <Chip
                    style={{ paddingLeft: '.5rem' }}
                    label={input}
                    key={input}
                    color="error"
                    icon={<FontAwesomeIcon icon={'times'} />}
                  />
                ))}
                {notFoundInputs.map(input => (
                  <Chip
                    style={{ paddingLeft: '.5rem' }}
                    label={input}
                    key={input}
                    icon={<FontAwesomeIcon icon={'question'} />}
                  />
                ))}
              </>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {hasValue(parameter.value) ? (
              <>
                <span style={{ fontWeight: 'bold' }}>
                  {parameter.type === ParameterType.Multiple
                    ? (parameter.value as any[]).join(', ')
                    : parameter.value}
                </span>
              </>
            ) : parameter.type === ParameterType.Multiple ? (
              <>
                <span style={{ color: 'grey' }}>{`Array<`}</span>
                <span>
                  {parameter.valueType} {parameter.optional && ' | undefined'}
                </span>
                <span style={{ color: 'grey' }}>{`>`}</span>
              </>
            ) : (
              <span>
                {parameter.valueType} {parameter.optional && ' | undefined'}
              </span>
            )}
          </div>
        )}
      </InputPresetWrapper>
    </div>
  );
};

export default ExecutionParameter;
